import type { CourseLocaleData } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@lms/ui/card';
import { CheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLocale, useTranslations } from 'use-intl';
import { useBrand } from '@/features/branding/brand-provider';

export interface StudentCourse {
  id: string;
  courseId: number;
  curriculumName: string;
  curriculumNameTranslated: Record<string, string> | null;
  courseData: Record<string, CourseLocaleData>;
  status: 'ongoing' | 'passed' | null;
  image: string | null;
  ordering: number | null;
}

export function CourseCard({ course }: { course: StudentCourse }) {
  const { slug } = useBrand();
  const locale = useLocale();
  const t = useTranslations('me');
  return (
    <Card
      className="card bg-background h-fit w-96 shadow-xl"
      data-testid="course-card"
      data-course-id={course.courseId}
      data-course-status={course.status ?? 'not_started'}
    >
      <CardHeader>
        <CardTitle data-testid="course-title">
          {course.courseData?.[locale]?.name ||
            course.courseData?.en?.name ||
            Object.values(course.courseData ?? {})[0]?.name}
        </CardTitle>
        <CardDescription data-testid="course-status">
          {t(`course_status.${course.status ?? 'not_started'}`)}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <img
          src={course.image ?? '/placeholder.svg'}
          alt={t('course_image_alt')}
          className="mx-auto rounded object-scale-down"
          data-testid="course-image"
        />
      </CardContent>
      <CardFooter className="flex-col gap-4">
        {course.status === 'passed' ? (
          <Button
            className="w-full"
            variant="outline"
            disabled
            data-testid="course-passed-button"
          >
            <CheckIcon /> {t('course_status.passed')}
          </Button>
        ) : (
          <Button className="w-full" asChild data-testid="go-to-course-button">
            <Link to={`/${slug}/course/${course.id}`} target="_blank">
              {t('go_to_course')}
            </Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
